import {
	TicketDTO,
	TicketDivisionDTO,
	TicketLineDTO,
	ClosingQuantityDTO,
	InvoiceParamsFromPosDTO,
	SaleDTO,
	Res
} from 'core/api';
import { Result, CustomError, handleServerErrors, ServerError, Events } from 'core/infrastructure';

import { OrderingFacade, SplitTicketFacade } from 'pos/facades';
import { Command } from 'pos/common';

type LastOperation = 'close' | 'park' | 'move';

let closingTicket: Nullable<TicketDTO> = null;
let originalTicket: Nullable<TicketDTO> = null;
let lastOperation: Nullable<LastOperation> = null;

export async function createDivision(ticket: TicketDTO): Promise<Result<TicketDivisionDTO, CustomError>> {

	const result = await safeSplit(() => {
		return SplitTicketFacade.createDivision(ticket);
	}, 'left');

	if (result.ok) {
		originalTicket = result.value.Left;
		if (originalTicket.Lines.length === 0)
			await Events.raise('pos:split-ticket:completed', { operation: lastOperation! });
	}

	return result;
}

export function divideEvenly(division: TicketDivisionDTO, pendingParts: number): Promise<Result<TicketDivisionDTO, CustomError>> {
	return safeSplit(() => {
		return SplitTicketFacade.divideEvenly(division, pendingParts);
	}, 'left');
}

export function moveOneUnitToRight(division: TicketDivisionDTO, line: TicketLineDTO): Promise<Result<TicketDivisionDTO>> {
	return safeSplit(() => {
		return SplitTicketFacade.moveOneUnitToRight(division, line);
	}, 'left');
}

export function moveOnePartToRight(division: TicketDivisionDTO, line: TicketLineDTO, numberOfParts: number): Promise<Result<TicketDivisionDTO>> {
	return safeSplit(() => {
		return SplitTicketFacade.moveOnePartToRight(division, line, numberOfParts);
	}, 'left');
}

export function moveOneUnitToLeft(division: TicketDivisionDTO, line: TicketLineDTO): Promise<Result<TicketDivisionDTO>> {
	return safeSplit(() => {
		return SplitTicketFacade.moveOneUnitToLeft(division, line);
	}, 'left');
}

export function moveOneTableCompanionToRight(division: TicketDivisionDTO): Promise<Result<TicketDivisionDTO>> {
	return safeSplit(() => {
		return SplitTicketFacade.moveOneTableCompanionToRight(division);
	}, 'left');
}

export function moveOneTableCompanionToLeft(division: TicketDivisionDTO): Promise<Result<TicketDivisionDTO>> {
	return safeSplit(() => {
		return SplitTicketFacade.MoveOneTableCompanionToLeft(division);
	}, 'left');
}

export async function updateClosingTicket(): Promise<void> {

	if (closingTicket === null)
		return;

	const result = await safeSplit(() => {
		return OrderingFacade.getTicket(getClosingTicket().Id);
	}, 'right');

	if (result.ok)
		closingTicket = result.value;
}

export async function beginClosingTicket(closingQuantities: ClosingQuantityDTO[], closingTableCompanions: Nullable<number>): Promise<Result> {

	const result = await safeSplit(() => {

		return SplitTicketFacade.beginClosingTicket(getOriginalTicket().Id, closingQuantities, closingTableCompanions);
	}, 'left');

	if (result.ok)
		closingTicket = result.value;

	return result;
}

export async function cancelClosingTicket(): Promise<Result> {

	return safeSplit(async () => {

		// Ignoramos los errores de TicketNotFound, TicketFrozen, EmptyTicket porque eso quiere decir que de alguna otra forma
		// antes de que cancelemos el ticket que estabamos usando como cuenta parcial, alguien lo ha borrado, cerrado, 
		// etc. y no hace falta hacer nada porque ya no podemos hacer nada con ese ticket. En esos casos también establecemos a null
		// el closingTicket
		return await handleServerErrors(async () => {
			const result = await SplitTicketFacade.cancelClosingTicket(getClosingTicket().Id, getOriginalTicket().Id);
			closingTicket = null;
			return result;
		}, {
			onTicketNotFoundError: async (_error) => { closingTicket = null; },
			onFrozenTicketError: async (_error) => { closingTicket = null; },
			onEmptyTicketError: async (_error) => { closingTicket = null; },
		});
	}, 'right');
}

export async function endClosingTicket(
	closingQuantities: ClosingQuantityDTO[],
	originalTableCompanions: Nullable<number>): Promise<Result> {

	return safeSplit(async () => {
		await SplitTicketFacade.endClosingTicket(getOriginalTicket().Id, getClosingTicket().Id, closingQuantities, originalTableCompanions);
		closingTicket = null;
	}, 'right');
}

export async function parkClosingTicket() {
	const result = await safeSplit(() => SplitTicketFacade.moveTicket(getClosingTicket(), true, getClosingTicket().Location), 'right');
	if (result.ok) {
		closingTicket = result.value;
		lastOperation = 'park';
	}

	return result;
}

export async function moveClosingTicket() {
	const result = await Command.execute('moveTicket', getClosingTicket(), getOriginalTicket());
	if (result.ok) {
		closingTicket = result.value;
		lastOperation = 'move';
	}

	return result;
}

export async function invoiceClosingTicket(params: InvoiceParamsFromPosDTO): Promise<Result<SaleDTO, CustomError>> {

	return safeSplit(async () => {
		const result = await OrderingFacade.closeTicket(getClosingTicket().Id, params);
		lastOperation = 'close';
		await Events.raise('pos:sale:splitted-ticket-closed', { invoiceId: result.DocumentId, invoiceType: params.InvoiceType });
		return result;
	}, 'right');
}

export function getClosingTicket(): TicketDTO {

	if (closingTicket === null)
		throw new Error('No se ha iniciado ninguna operación con la cuenta parcial');

	return closingTicket;
}

export function getOriginalTicket(): TicketDTO {

	if (originalTicket === null)
		throw new Error('No se ha iniciado ninguna división');

	return originalTicket;
}

export async function safeSplit<T>(fn: () => Promise<T>, targetTicket: 'left' | 'right'): Promise<Result<T, CustomError>> {

	let handledError: CustomError = new ServerError('ERROR NO MANEJADO');

	const result = await handleServerErrors(fn, {

		onEmptyTicketError: async error => {
			handledError = error;
			await Events.raise('pos:split-ticket:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_MODIFIED_AND_NOW_IT_IS_EMPTY(), error, targetTicket });
		},

		onPrintError: async error => {
			handledError = error;
			await Events.raise('pos:split-ticket:operation-failed', { msg: Res.Msg.MSG_TICKET_COULD_NOT_BE_PRINTED(error.message), error, targetTicket });
		},

		onInvalidPaymentError: async error => {
			handledError = error;
			await Events.raise('pos:split-ticket:operation-failed', { msg: error.message, error, targetTicket });
		},

		onDomainError: async error => {
			handledError = error;
			await Events.raise('pos:split-ticket:operation-failed', { msg: error.message, error, targetTicket });
		},

		onTicketNotFoundError: async error => {
			handledError = error;
			await Events.raise('pos:split-ticket:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER(), error, targetTicket });
		},

		onFrozenTicketError: async error => {
			handledError = error;
			await Events.raise('pos:split-ticket:operation-failed', { msg: Res.Msg.MSG_CANNOT_MODIFY_FROZEN_TICKET(), error, targetTicket });
		},

		onTicketSynchronizationError: async error => {
			handledError = error;

			await Events.raise('pos:split-ticket:operation-failed', { msg: Res.Msg.MSG_TICKET_HAS_BEEN_MODIFIED_SO_CHECK_THE_NEW_COMPOSITION(), error, targetTicket });
		},

		onIntegrationAuthorizationError: async error => {
			await Events.raise('pos:split-ticket:operation-failed', { msg: Res.Msg.MSG_INTEGRATION_AUTHORIZATION_ERROR(error.message), error, targetTicket });
			handledError = error;
		},

		onInvalidCustomerCifError: async error => {
			await Events.raise('pos:split-ticket:operation-failed', { msg: Res.Msg.MSG_INVALID_CUSTOMER_CIF_TO_CREATE_STANDARD_INVOICE(), error, targetTicket });
			handledError = error;
		},


	});

	return result.ok ? Result.ok(result.value) : Result.error(handledError);
}